import { ArrowRight, Check, HandHeart } from "lucide-react";
import { cn } from "@/lib/utils";

const SUPPORT = {
  load: (f, n) => ({ head: `${f} is carrying more than usual.`, body: `${n} people is more than most leaders care for comfortably. Sharing a few could lighten the load.` }),
  overdue: (f) => ({ head: `${f} may need a hand.`, body: "A few follow-ups have slipped past their date." }),
  quiet: (f) => ({ head: `${f} may need support.`, body: "No care has been logged by them in the last month. A friendly check-in could mean a lot." }),
};

const Facts = ({ s }) => (
  <p className="text-[15px] text-ink-soft mt-1" data-testid={`leader-facts-${s.id}`}>
    {[`${s.entrusted} people entrusted`, `${s.within} within Care Rhythm`, s.attention && `${s.attention} need attention`, `${s.connections_week} meaningful ${s.connections_week === 1 ? "connection" : "connections"} this week`].filter(Boolean).join(" · ")}
  </p>
);

const Avatar = ({ name, size }) => {
  const [f, l] = name.split(" ");
  return <span className="shrink-0 rounded-full bg-sunk text-ink-soft font-serif inline-flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.38 }}>{f[0]}{(l || "")[0]}</span>;
};

const SupportCard = ({ s, open }) => {
  const first = s.name.split(" ")[0];
  const st = SUPPORT[s.support[0]](first, s.entrusted);
  return (
    <div className="surface p-6" data-testid={`leader-flag-${s.id}`}>
      <div className="flex items-center gap-4"><Avatar name={s.name} size={56} /><div className="min-w-0"><p className="t-name-lg text-[24px]">{s.name}</p><Facts s={s} /></div></div>
      <div className="mt-5 rounded-2xl bg-sun-soft px-5 py-4 flex gap-3" data-testid={`leader-support-${s.id}`}>
        <HandHeart size={20} className="text-sun shrink-0 mt-0.5" />
        <p className="text-[16px] text-ink leading-relaxed"><span className="font-semibold">{st.head}</span> {st.body}</p>
      </div>
      <button className="btn btn-outline btn-sm mt-5" onClick={open} data-testid={`leader-open-${s.id}`}>Open {first}'s people</button>
    </div>
  );
};

const GroupHead = ({ dot, label, count, hint, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <span className={cn("h-2.5 w-2.5 rounded-full mt-2 shrink-0", dot)} />
    <div><p className="text-[17px] text-ink font-semibold">{label} <span className="text-ink-faint font-normal">· {count}</span></p><p className="text-sm text-ink-faint mt-0.5">{hint}</p></div>
  </div>
);

export const CareTeam = ({ d, openList }) => {
  const open = (s) => openList(`leader:${s.id}`, `${s.name.split(" ")[0]}'s people`);
  const support = d.team.filter((s) => s.support.length);
  const attention = d.team.filter((s) => !s.support.length && s.attention);
  const caught = d.team.filter((s) => !s.support.length && !s.attention);
  const link = "text-ink font-semibold underline decoration-line underline-offset-4 hover:text-brand";
  return (
    <section data-testid="overview-care-team">
      <h2 className="t-heading">Your care team</h2>
      <p className="text-lg text-ink-soft mt-2 leading-relaxed max-w-3xl">
        {d.team.length} {d.team.length === 1 ? "leader is" : "leaders are"} caring for <button className={link} onClick={() => openList("covered", "People with an Assigned Care Leader")} data-testid="team-covered-link">{d.metrics.covered} people</button>.{" "}
        <button className={link} onClick={() => openList("no_caregiver", "People without an Assigned Care Leader")} data-testid="team-uncovered-link">{d.metrics.no_caregiver} people</button> still need someone.
      </p>
      <p className="text-sm text-ink-faint mt-2">Grouped by how the people in their care are doing, never ranked.</p>
      {support.length > 0 && (
        <div className="mt-10" data-testid="team-group-support">
          <GroupHead dot="bg-sun" label="May need support" count={support.length} hint="A conversation or a little help could go a long way." />
          <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 mt-5">{support.map((s) => <SupportCard key={s.id} s={s} open={() => open(s)} />)}</div>
        </div>
      )}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-start [&>*]:min-w-0">
        {attention.length > 0 && (
          <div data-testid="team-group-attention">
            <GroupHead dot="bg-ink-faint" label="A few people need attention" count={attention.length} hint="Caring well. A few people are ready for a check-in or follow-up." />
            <div className="mt-4 border-y border-line divide-y divide-line">
              {attention.map((s) => (
                <button key={s.id} onClick={() => open(s)} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={`load-row-${s.id}`}>
                  <Avatar name={s.name} size={46} />
                  <span className="flex-1 min-w-0"><span className="block text-[17px] text-ink font-medium leading-tight group-hover:text-brand transition-colors">{s.name}</span><Facts s={s} /></span>
                  <ArrowRight size={18} className="text-ink-faint group-hover:text-brand shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
        {caught.length > 0 && (
          <div data-testid="team-group-caught-up">
            <GroupHead dot="bg-calm" label="All caught up" count={caught.length} hint="Everyone in their care is within rhythm and follow-ups are on track." />
            <div className="mt-4 tint bg-calm-soft p-2 grid gap-1.5">
              {caught.map((s) => (
                <button key={s.id} onClick={() => open(s)} className="w-full flex items-center gap-3 rounded-2xl bg-surface px-3 py-2.5 text-left" data-testid={`load-row-${s.id}`}>
                  <span className="relative shrink-0"><Avatar name={s.name} size={40} /><span className="absolute -right-0.5 -bottom-0.5 h-4 w-4 rounded-full bg-calm text-white flex items-center justify-center ring-2 ring-surface"><Check size={10} strokeWidth={3} /></span></span>
                  <span className="min-w-0"><span className="block text-ink font-medium leading-tight">{s.name}</span><span className="block text-sm text-ink-faint">{s.entrusted} people entrusted · {s.connections_week} this week</span></span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-sm text-ink-faint mt-8">Most leaders care for about {d.comfortable_load} people comfortably.</p>
    </section>
  );
};
